import React from 'react';
import { Button } from './button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const PAGE_SIZES = [10, 25, 50, 100];

export default function Pagination({ total = 0, page = 1, pageSize = 25, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, page - 1, page, page + 1]);
    return [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  };

  const pages = getPages();

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t bg-white text-sm font-ibm">
      <div className="flex items-center gap-3">
        <span className="text-text-secondary whitespace-nowrap">
          {total === 0 ? 'No records' : `Showing ${from}–${to} of ${total}`}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-text-secondary text-xs">Per page:</span>
          <Select value={String(pageSize)} onValueChange={v => { onPageSizeChange(Number(v)); onPageChange(1); }}>
            <SelectTrigger className="h-7 w-16 text-xs font-ibm" data-testid="pagination-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map(s => (
                <SelectItem key={s} value={String(s)} className="text-xs">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === 1}
          onClick={() => onPageChange(1)} data-testid="pagination-first">
          <ChevronsLeft className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === 1}
          onClick={() => onPageChange(page - 1)} data-testid="pagination-prev">
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>

        {pages.map((p, i) => {
          const prev = pages[i - 1];
          return (
            <React.Fragment key={p}>
              {prev && p - prev > 1 && (
                <span className="px-1 text-text-secondary text-xs">…</span>
              )}
              <Button
                variant={p === page ? 'default' : 'ghost'}
                size="icon"
                className={`h-7 w-7 text-xs ${p === page ? 'bg-accent-blue hover:bg-accent-blue/90 text-white' : ''}`}
                onClick={() => onPageChange(p)}
                data-testid={`pagination-page-${p}`}
              >
                {p}
              </Button>
            </React.Fragment>
          );
        })}

        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === totalPages}
          onClick={() => onPageChange(page + 1)} data-testid="pagination-next">
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === totalPages}
          onClick={() => onPageChange(totalPages)} data-testid="pagination-last">
          <ChevronsRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
